import { DialogLevel, useDialogContext } from '@/components/contexts/ModalContext';
import { CancelButton } from '@/components/formLayouts/CancelButton';
import { FormContainer } from '@/components/formLayouts/FormContainer';
import { SubmitButton } from '@/components/formLayouts/SubmitButton';
import { useGlobalCurrency } from '@/resources/organizationSettings/currencySettingsHandler';
import { getRandomUuidv4 } from '@/utils/randomizerFunctions';
import { Trans, t } from '@lingui/macro';
import {
    DestructivePrimaryButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    SecondaryButton,
    Text,
} from '@luminovo/design-system';
import {
    Packaging,
    TotalCostOfOwnershipRuleCategory,
    TotalCostOfOwnershipRuleConditionType,
} from '@luminovo/http-client';
import { TcoRuleForm } from './TcoRuleForm';
import { TcoRuleFormState } from './type';

export function useAddTcoRuleDialog() {
    const { setDialog, closeDialog } = useDialogContext(DialogLevel.Dynamic);
    const { preferredCurrency } = useGlobalCurrency();

    // TODO I think it would be better if that would be a normal DTO, and we are faking the id.
    // Because we have to handle the is_valid field.
    const defaultValues: TcoRuleFormState = {
        id: `NEW_RULE_${getRandomUuidv4()}`,
        name: '',
        description: '',
        category: TotalCostOfOwnershipRuleCategory.Packaging,
        action: {
            type: 'Absolute',
            value: {
                amount: '0',
                currency: preferredCurrency,
            },
        },
        conditions: [
            {
                condition_type: TotalCostOfOwnershipRuleConditionType.Packaging,
                value: Packaging.ReReel,
            },
        ],
        is_valid: true,
        updated_at: new Date().toISOString(),
    };

    return {
        openDialog: (onSubmit: (values: TcoRuleFormState) => void) =>
            setDialog(
                <Dialog open={true} maxWidth={'md'} fullWidth={true} onClose={() => closeDialog()}>
                    <FormContainer
                        defaultValues={defaultValues}
                        onSubmit={(values) => {
                            onSubmit(values);
                            closeDialog();
                        }}
                    >
                        <DialogTitle
                            title={t`Add total cost of ownership rule`}
                            sticky={true}
                            onClose={() => closeDialog()}
                        />
                        <DialogContent>
                            <TcoRuleForm />
                        </DialogContent>
                        <DialogActions sticky={true}>
                            <CancelButton onClick={closeDialog} />
                            <SubmitButton />
                        </DialogActions>
                    </FormContainer>
                </Dialog>,
            ),
        closeDialog,
    };
}

export function useEditTcoRuleDialog(rule: TcoRuleFormState) {
    const { setDialog, closeDialog } = useDialogContext(DialogLevel.Dynamic);

    return {
        openDialog: (onSubmit: (values: TcoRuleFormState) => void) =>
            setDialog(
                <Dialog open={true} maxWidth={'md'} fullWidth={true} onClose={() => closeDialog()}>
                    <FormContainer
                        defaultValues={rule}
                        onSubmit={(values) => {
                            onSubmit(values);
                            closeDialog();
                        }}
                    >
                        <DialogTitle
                            title={t`Edit total cost of ownership rule`}
                            sticky={true}
                            onClose={() => closeDialog()}
                        />
                        <DialogContent>
                            <TcoRuleForm />
                        </DialogContent>
                        <DialogActions sticky={true}>
                            <CancelButton onClick={closeDialog} />
                            <SubmitButton />
                        </DialogActions>
                    </FormContainer>
                </Dialog>,
            ),
        closeDialog,
    };
}

export function useDeleteTcoRuleDialog() {
    const { setDialog, closeDialog } = useDialogContext(DialogLevel.Dynamic);

    return {
        openDialog: (onSubmit: () => void) =>
            setDialog(
                <Dialog open={true} maxWidth={'xs'} fullWidth={true} onClose={() => closeDialog()}>
                    <DialogTitle title={t`Delete total cost of ownership rule`} onClose={() => closeDialog()} />
                    <DialogContent>
                        <Text>
                            <Trans>Are you sure you want to delete this rule?</Trans>
                        </Text>
                    </DialogContent>
                    <DialogActions>
                        <SecondaryButton onClick={() => closeDialog()}>
                            <Trans>Cancel</Trans>
                        </SecondaryButton>
                        <DestructivePrimaryButton
                            onClick={() => {
                                onSubmit();
                                closeDialog();
                            }}
                        >
                            <Trans>Yes, delete</Trans>
                        </DestructivePrimaryButton>
                    </DialogActions>
                </Dialog>,
            ),
        closeDialog,
    };
}
